import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tours.css";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { moduleForPath, projectPageModule } from "@/components/layout/navConfig";
import { tourFor } from "@/tours/tours";
import { guideFor, journeySteps, workflowById } from "@/tours/guides";

/** Narrower than this (phones) gets no tour: the menus are different there. */
export const TOUR_MIN_WIDTH = 768;

const onScreen = (el) => Boolean(el) && el.getClientRects().length > 0;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const bigEnough = () => typeof window !== "undefined" && window.innerWidth >= TOUR_MIN_WIDTH;
const openProjectId = () => window.location.pathname.match(/^\/projects\/([^/]+)/)?.[1] || null;

const baseConfig = (steps, onDone) => {
  let reported = false;
  return {
    steps,
    showProgress: true,
    allowClose: true,
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Finish",
    popoverClass: "bn-tour",
    onPopoverRender: (popover) => {
      /* a modal form closes when something outside it is pressed; the guide sits outside it, so keep its clicks to itself */
      ["pointerdown", "mousedown", "touchstart"].forEach((ev) => popover.wrapper.addEventListener(ev, (e) => e.stopPropagation()));
    },
    onDestroyed: () => {
      if (reported) return;
      reported = true;
      onDone?.();
    },
  };
};

/** Runs `allSteps`, leaving out steps whose element is not on screen, so a hidden menu item never breaks the tour.
 *  `onDone` runs once when it is finished or closed. Returns false when nothing was started. */
function run(allSteps, onDone, minSteps = 1) {
  if (!bigEnough()) return false;
  const steps = allSteps.filter((s) => !s.element || onScreen(document.querySelector(s.element)));
  if (steps.length < minSteps) return false;
  driver(baseConfig(steps, onDone)).drive();
  return true;
}

/** The first-time tour for `role`. */
export function startTour(role, onDone) {
  return run(tourFor(role), onDone);
}

/** The module overview (with the role's own intro). Needs at least two steps left on screen to be worth showing. */
export function startModuleGuide(moduleId, role, onDone) {
  return run(guideFor(moduleId, role), onDone, 2);
}

/** Waits until `selector` is on screen (or `ms` has passed). A page needs a moment to load its data and buttons. */
async function waitFor(selector, ms = 1800) {
  const end = Date.now() + ms;
  while (Date.now() < end) {
    if (!selector || onScreen(document.querySelector(selector))) return true;
    await sleep(100);
  }
  return false;
}

/* a form that is open (not the guide's own popover, which is also marked role="dialog") */
const DIALOG = '[role="dialog"][data-state="open"]';
const dialogOpen = () => onScreen(document.querySelector(DIALOG));

/** Closes a form that is open (the same as pressing Escape). */
async function closeDialog() {
  if (!dialogOpen()) return;
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
  for (let i = 0; i < 15 && dialogOpen(); i++) await sleep(100);
}

/** Turns workflow steps into driver steps that open their own page when reached, going forward or back.
 *  A step with `form` lives inside the form opened by that button: the form is opened when needed and left open
 *  for the next field. Any other step closes an open form first. The guide only points; it never saves a form.
 *  A step whose button is not on the page still shows, centred, so the explanation is never lost. */
function workflowSteps(steps, navigate) {
  let d = null;
  let moving = false;                                  // ignore extra clicks while the next page is opening
  const arrive = async (s) => {
    if (!s.form) await closeDialog();
    if (s.to && window.location.pathname !== s.to) { await closeDialog(); navigate(s.to); }
    if (s.form && !dialogOpen()) {
      await waitFor(s.form);
      document.querySelector(s.form)?.click();
      await waitFor(DIALOG, 2500);
      await sleep(300);                                // the form slides in
    }
    await waitFor(s.el);
    await sleep(250);
  };
  const driverSteps = steps.map((s, i) => ({
    /* a field that only appears after an earlier choice is not there yet: highlight the whole form instead */
    element: s.el ? () => document.querySelector(s.el) || (s.form ? document.querySelector(`${DIALOG} form`) || document.querySelector(DIALOG) : null) || undefined : undefined,
    popover: {
      title: s.title,
      description: s.description,
      side: "bottom",
      onNextClick: async () => {
        if (moving) return;
        if (i === steps.length - 1) { d.destroy(); return; }
        moving = true;
        try { await arrive(steps[i + 1]); d.moveNext(); } finally { moving = false; }
      },
      onPrevClick: async () => {
        if (moving || i === 0) return;
        moving = true;
        try { await arrive(steps[i - 1]); d.movePrevious(); } finally { moving = false; }
      },
    },
  }));
  return { driverSteps, setDriver: (x) => { d = x; }, first: () => arrive(steps[0]) };
}

/** startTour() for the signed-in user, plus the on-demand guides. */
export function useRoleTour() {
  const { user, refreshMe } = useAuth();
  const { canView } = usePermissions();
  const navigate = useNavigate();
  const alreadyDone = Boolean(user?.tour_completed_at);
  const role = user?.role;

  /* first-time tour: finishing or closing it is saved once */
  const launch = useCallback(
    () => startTour(role, async () => {
      if (alreadyDone) return;                       // a replay: nothing to save
      try {
        await api.post("/auth/tour-complete");
        await refreshMe();
      } catch {
        /* not saved: the tour will simply show again next time */
      }
    }),
    [role, alreadyDone, refreshMe]
  );

  /* the module overview, with the role's own sentence; never saved */
  const startGuide = useCallback((moduleId) => startModuleGuide(moduleId, role), [role]);

  /* can this user open this page? (a page they may not open is left out of a guide) */
  const pageOk = useCallback((path) => {
    const m = path.match(/^\/projects\/[^/]+\/(.*)$/);
    return m ? canView(projectPageModule(m[1].replace(/\/$/, ""))) : canView(moduleForPath(path));
  }, [canView]);

  /* opens each page in turn, explaining the job at each one */
  const walk = useCallback(async (steps) => {
    if (steps.length === 0) return false;
    const built = workflowSteps(steps, navigate);
    await built.first();
    const d = driver({ ...baseConfig(built.driverSteps), onDestroyed: closeDialog });   // leaving the guide also closes any form it opened
    built.setDriver(d);
    d.drive();
    return true;
  }, [navigate]);

  /* a workflow of one module; never saved */
  const startWorkflow = useCallback(async (moduleId, workflowId) => {
    const wf = workflowById(moduleId, workflowId);
    if (!wf || !bigEnough()) return false;
    const projectId = openProjectId();
    if (wf.project && !projectId) return false;
    return walk(wf.steps.map((s) => ({ ...s, to: s.to.replace(":projectId", projectId || "") })).filter((s) => pageOk(s.to)));
  }, [pageOk, walk]);

  /* the whole job, start to finish (from `stageId` onward); never saved */
  const startJourney = useCallback(async (stageId) => {
    if (!bigEnough()) return false;
    const all = journeySteps(stageId);
    let projectId = openProjectId();
    if (!projectId && all.some((s) => s.to.includes(":projectId"))) {
      try { projectId = (await api.get("/projects")).data?.[0]?.id || null; } catch { projectId = null; }   // no project open: use the user's first
    }
    return walk(all
      .filter((s) => !s.to.includes(":projectId") || projectId)
      .map((s) => ({ ...s, to: s.to.replace(":projectId", projectId || "") }))
      .filter((s) => pageOk(s.to)));
  }, [pageOk, walk]);

  return { startTour: launch, startGuide, startWorkflow, startJourney };
}
